import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectPageConfigurationData,
  selectPageConfigurationIsInitialized,
  selectPageConfigurationIsLoading,
} from '@/store/PageConfiguration/PageConfigurationSelectors'
import { initializeContractData, updateContractConfigData, updateContractDefaultData } from './SubscriptionFormSlice'
import { mapContactSubscriptionData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Contact/mapContactSubscriptionData'
import { mapAddressSubscriptionData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Address/mapAddressSubscriptionData'
import { subscriptionFormActionsV2 } from './SubscriptionFormV2Sagas'
import { selectConfig } from './SubscriptionFormSelectors'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import useTabBar from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useTabBar'
import { useLocation, useNavigate } from 'react-router'

const useSubscriptionForm = id => {
  const dispatch = useDispatch()
  const initialData = useSelector(selectPageConfigurationData)
  const isInitialized = useSelector(selectPageConfigurationIsInitialized)
  const pageConfigurationIsLoading = useSelector(selectPageConfigurationIsLoading)
  const { contactId } = useSelector(selectConfig)
  const { country } = useSelector(selectAuthData)
  const { pushToOpenedContracts, existingContracts } = useTabBar()
  const navigate = useNavigate()
  const { hash, pathname, search } = useLocation()
  // Guards the "open contract from url hash" effect so it pushes only once per hash.
  const openedHashRef = useRef(null)

  useEffect(() => {
    if (!isInitialized || pageConfigurationIsLoading || !initialData) return
    const contractData = {
      contact: mapContactSubscriptionData(initialData.contact ?? {}, country),
      addresses: mapAddressSubscriptionData(initialData),
      contracts: initialData?.contact?.contracts ?? {},
      contactId: initialData.contact?.id,
      comments: initialData?.contact?.comments ?? [],
      recentBankings: initialData?.contact?.recentBankings ?? [],
    }

    dispatch(initializeContractData(contractData))
  }, [initialData])

  // Load a given contract id from url hash. The hash is only a quick-access deep link to open a
  // specific contract; once that contract is opened we strip it from the URL. This keeps it out
  // of the way while navigating between tabs and, crucially, prevents this effect (which depends
  // on `existingContracts`, i.e. re-runs on every contract mutation) from re-pushing a stale
  // whole-contract snapshot and clobbering fresh state. Contracts load asynchronously, so the
  // effect keeps re-running until the contract for the hash is available; the ref guard makes the
  // push happen only once.
  useEffect(() => {
    if (!hash || !existingContracts || Object.keys(existingContracts).length === 0) return
    const hashId = hash.replace('#', '')
    if (openedHashRef.current === hashId) return

    // Remove the hash from the URL once we have opened the target contract.
    const clearHash = () => navigate(`${pathname}${search}`, { replace: true })

    if (existingContracts[hashId]) {
      openedHashRef.current = hashId
      pushToOpenedContracts(existingContracts[hashId])
      clearHash()
      return
    }
    //search by Zoho contract id if not found by id
    const contractByZohoId = Object.values(existingContracts).find(contract => contract.zohoContractId === hashId)
    if (contractByZohoId) {
      openedHashRef.current = hashId
      pushToOpenedContracts(contractByZohoId)
      clearHash()
      return
    }
    alert('Contract not found for id: ' + hashId)
  }, [hash, existingContracts])

  // Contract status update listener for all contracts of given Zoho contact ID
  useEffect(() => {
    if (!id) return

    window.Echo.channel(`contract.status.${id}`).listen('.operation.success', e => {
      if (e.data && e.data.contractId) {
        dispatch(
          updateContractDefaultData({
            id: e.data.contractId,
            data: { status: e.data.status, transfertLog: e.data.transfertLog || null },
          }),
        )
        dispatch(
          updateContractConfigData({
            id: e.data.contractId,
            data: {
              resendAllowed: e.data.resendAllowed,
              isEditable: e.data.isEditable,
              ...(e.data.status === 'TRERR' ? { saveSuccess: false } : {}),
            },
          }),
        )
      }
    })

    return () => {
      window.Echo.leaveChannel(`contract.status.${id}`)
    }
  }, [id])

  // Load VA data
  useEffect(() => {
    if (!contactId) return
    dispatch({ type: subscriptionFormActionsV2.LOAD_ADDITIONAL_SALES_CONFIG })
    dispatch({ type: subscriptionFormActionsV2.LOAD_EXISTING_ADDITIONAL_SALES, contactId })
  }, [contactId])
}

export default useSubscriptionForm
